/**
 * SaaS Authentication Service
 * Handles sign up, sign in, workspace switching for platform administrators
 */

const API_BASE_URL = 'https://dev-platform-api-dev.benjiemalinao879557.workers.dev';

export interface SaasAccount {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'admin' | 'support' | 'developer';
  status: 'active' | 'suspended' | 'pending_verification';
  emailVerified: boolean;
  lastLoginAt?: string;
  createdAt: string;
  avatarUrl?: string;
  preferences?: Record<string, any>;
}

export interface WorkspaceAccess {
  id: string;
  organizationId: string;
  organizationName: string;
  accessLevel: 'read' | 'write' | 'admin';
  grantedAt: string;
  expiresAt?: string;
  reason: string;
  status: 'active' | 'revoked';
}

export interface AuthSession {
  token: string;
  account: SaasAccount;
  workspaces: WorkspaceAccess[];
  currentWorkspace?: WorkspaceAccess;
  expiresAt: string;
}

export interface SignUpRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  // Note: role is not included in public signup - new customers get standard user role
}

export interface SignInRequest {
  email: string;
  password: string;
}

class AuthService {
  private baseUrl: string;
  private session: AuthSession | null = null;
  private readonly TOKEN_KEY = 'saas_auth_token';
  private readonly SESSION_KEY = 'saas_auth_session';

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
    this.loadSessionFromStorage();
  }

  /**
   * Load session from localStorage on initialization
   */
  private loadSessionFromStorage() {
    try {
      const sessionData = localStorage.getItem(this.SESSION_KEY);
      if (sessionData) {
        const session = JSON.parse(sessionData);
        if (new Date(session.expiresAt) > new Date()) {
          // Auto-select first workspace if none is currently selected
          if (session.workspaces?.length > 0 && !session.currentWorkspace) {
            session.currentWorkspace = session.workspaces[0];
            this.saveSessionToStorage(session); // Save the updated session
          }
          this.session = session;
        } else {
          this.clearSession();
        }
      }
    } catch (error) {
      console.error('Failed to load session from storage:', error);
      this.clearSession();
    }
  }

  /**
   * Save session to localStorage
   */
  private saveSessionToStorage(session: AuthSession) {
    try {
      localStorage.setItem(this.SESSION_KEY, JSON.stringify(session));
      localStorage.setItem(this.TOKEN_KEY, session.token);
    } catch (error) {
      console.error('Failed to save session to storage:', error);
    }
  }

  /**
   * Clear session from memory and storage
   */
  private clearSession() {
    this.session = null;
    localStorage.removeItem(this.SESSION_KEY);
    localStorage.removeItem(this.TOKEN_KEY);
  }

  /**
   * Make authenticated API request
   */
  private async request<T>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options.headers as Record<string, string>,
    };

    // Add auth token if available
    if (this.session?.token) {
      headers['Authorization'] = `Bearer ${this.session.token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      // Handle 401 - session expired
      if (response.status === 401) {
        this.clearSession();
        throw new Error('Session expired. Please sign in again.');
      }

      const error = await response.json().catch(() => ({ 
        message: `HTTP ${response.status}: ${response.statusText}` 
      }));
      throw new Error(error.message || `Request failed: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Sign up new SaaS account
   */
  async signUp(data: SignUpRequest): Promise<{ success: boolean; message: string }> {
    return this.request('/auth/signup', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Sign in to SaaS account
   */
  async signIn(data: SignInRequest): Promise<AuthSession> {
    const response = await this.request<{
      success: boolean;
      token: string;
      account: {
        id: string;
        email: string;
        firstName: string;
        lastName: string;
        role: string;
      };
    }>('/auth/signin', {
      method: 'POST',
      body: JSON.stringify(data),
    });

    // Convert API response to AuthSession format
    const session: AuthSession = {
      token: response.token,
      account: {
        id: response.account.id,
        email: response.account.email,
        firstName: response.account.firstName,
        lastName: response.account.lastName,
        role: response.account.role as 'admin' | 'support' | 'developer',
        status: 'active',
        emailVerified: true,
        createdAt: new Date().toISOString(),
      },
      workspaces: [], // Will be populated below
      expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // 24 hours
    };

    // Fetch workspaces for the authenticated user
    try {
      const workspacesResponse = await this.request<{ workspaces: WorkspaceAccess[] }>(`/auth/workspaces?accountId=${response.account.id}`, {
        headers: {
          'Authorization': `Bearer ${response.token}`,
        },
      });
      session.workspaces = workspacesResponse.workspaces;
      
      // Auto-select the first workspace as default (for tenant isolation)
      if (session.workspaces.length > 0 && !session.currentWorkspace) {
        session.currentWorkspace = session.workspaces[0];
      }
    } catch (error) {
      console.warn('Failed to fetch workspaces:', error);
      // Continue without workspaces - they can be loaded later
    }

    this.session = session;
    this.saveSessionToStorage(session);
    
    // Admin action logging disabled until endpoint is implemented
    // TODO: Implement /auth/log-action endpoint
    // this.logAdminAction('login', undefined, 'saas_account', session.account.id, {
    //   method: 'email_password'
    // });

    return session;
  }

  /**
   * Sign out (invalidate session)
   */
  async signOut(): Promise<void> {
    if (this.session) {
      try {
        await this.request('/auth/signout', {
          method: 'POST',
        });
      } catch (error) {
        console.error('Failed to sign out on server:', error);
      }
    }
    
    this.clearSession();
  }

  /**
   * Get current session
   */
  getSession(): AuthSession | null {
    return this.session;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.session !== null && new Date(this.session.expiresAt) > new Date();
  }

  /**
   * Get current account
   */
  getCurrentAccount(): SaasAccount | null {
    return this.session?.account || null;
  }

  /**
   * Get available workspaces
   */
  getWorkspaces(): WorkspaceAccess[] {
    return this.session?.workspaces || [];
  }

  /**
   * Get current workspace
   */
  getCurrentWorkspace(): WorkspaceAccess | null {
    return this.session?.currentWorkspace || null;
  }

  /**
   * Switch to a different customer workspace
   */
  async switchWorkspace(organizationId: string): Promise<WorkspaceAccess> {
    const accountId = this.session?.account?.id;
    if (!accountId) {
      throw new Error('No active session found');
    }
    
    const workspace = await this.request<WorkspaceAccess>(`/auth/switch-workspace?accountId=${accountId}`, {
      method: 'POST',
      body: JSON.stringify({ organizationId }),
    });

    if (this.session) {
      this.session.currentWorkspace = workspace;
      this.saveSessionToStorage(this.session);
    }

    // Admin action logging disabled until endpoint is implemented
    // TODO: Implement /auth/log-action endpoint
    // this.logAdminAction('workspace_switch', organizationId, 'organization', organizationId, {
    //   previous_workspace: this.session?.currentWorkspace?.organizationId,
    //   new_workspace: organizationId
    // });

    return workspace;
  }

  /**
   * Request password reset
   */
  async requestPasswordReset(email: string): Promise<{ success: boolean; message: string }> {
    return this.request('/auth/request-password-reset', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  /**
   * Reset password with token
   */
  async resetPassword(token: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    return this.request('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    });
  }

  /**
   * Verify email with token
   */
  async verifyEmail(token: string): Promise<{ success: boolean; message: string }> {
    return this.request('/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  /**
   * Update account profile
   */
  async updateProfile(data: Partial<Pick<SaasAccount, 'firstName' | 'lastName' | 'preferences'>>): Promise<SaasAccount> {
    const updatedAccount = await this.request<SaasAccount>('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });

    if (this.session) {
      this.session.account = updatedAccount;
      this.saveSessionToStorage(this.session);
    }

    return updatedAccount;
  }

  /**
   * Grant workspace access to another admin
   */
  async grantWorkspaceAccess(data: {
    email: string;
    organizationId: string;
    accessLevel: 'read' | 'write' | 'admin';
    reason: string;
    expiresIn?: number; // days
  }): Promise<WorkspaceAccess> {
    return this.request('/auth/grant-workspace-access', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Get admin audit log
   */
  async getAuditLog(filters?: {
    startDate?: string;
    endDate?: string;
    action?: string;
    organizationId?: string;
    limit?: number;
  }): Promise<Array<{
    id: string;
    action: string;
    timestamp: string;
    accountName: string;
    targetOrganization?: string;
    details: Record<string, any>;
    ipAddress?: string;
  }>> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) {
          params.set(key, value.toString());
        }
      });
    }

    return this.request(`/auth/audit-log?${params.toString()}`);
  }

  /**
   * Log admin action (internal method)
   */
  private async logAdminAction(
    action: string,
    targetOrganizationId?: string,
    targetResourceType?: string,
    targetResourceId?: string,
    details?: Record<string, any>
  ): Promise<void> {
    try {
      await this.request('/auth/log-action', {
        method: 'POST',
        body: JSON.stringify({
          action,
          targetOrganizationId,
          targetResourceType,
          targetResourceId,
          details,
        }),
      });
    } catch (error) {
      // Silently handle logging failures - this endpoint might not be implemented yet
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.debug('Admin action logging not available:', errorMessage);
      // Don't throw - logging failures shouldn't break the main flow
    }
  }

  /**
   * Refresh session token
   */
  async refreshSession(): Promise<AuthSession> {
    const session = await this.request<AuthSession>('/auth/refresh', {
      method: 'POST',
    });

    this.session = session;
    this.saveSessionToStorage(session);
    return session;
  }
}

// Export singleton instance
export const authService = new AuthService();
export default authService;